import { t } from "./i18n";

// typed wrapper around the preload bridge, or the hosted rpc on the web

interface Bridge {
  invoke(channel: string, ...args: unknown[]): Promise<unknown>;
  on(channel: string, listener: (payload: unknown) => void): void;
}

interface CloudInfo {
  url: string;
  token: string;
  extensionToken: string;
}

declare global {
  interface Window {
    bridge?: Bridge;
  }
}

const LOCAL_CHANNELS = new Set([
  "tabs:show", "tabs:hide", "tabs:reload", "tabs:home",
  "app:open-external", "cloud:info", "app:language", "plane:connect", "assistant:open-memory",
  "printers:list", "scans:inbox", "scans:capture", "scans:import", "scans:read"
]);

const LIVE_CHANNELS = [
  "logs:entry", "references:updated", "tasks:updated", "stats:updated",
  "assistant:chunk", "assistant:done",
  "notes:updated", "reminders:updated", "routines:updated", "routines:due", "goals:updated", "calendar:updated", "studio:updated", "ocs:updated", "doodles:updated", "board:updated", "scans:updated", "japanese:updated", "maps:updated", "roulette:updated",
  "shares:updated", "shared:updated", "boards:ops", "boards:updated", "boards:presence", "tierlists:updated", "tracker:updated", "music:updated", "awards:updated", "awards:unlocked", "feed:updated", "suggestions:updated", "poll:updated"
];

let cloud: CloudInfo | null = null;
let events: EventSource | null = null;
let eventsTimer = 0;
const listeners = new Map<string, Array<(payload: unknown) => void>>();

export const isWeb = (): boolean => !window.bridge;

function emit(channel: string, payload: unknown): void {
  (listeners.get(channel) || []).forEach((listener) => listener(payload));
}

function attachEvents(source: EventSource): void {
  source.onmessage = (message) => emit(message.type || "message", JSON.parse(message.data || "{}"));
  LIVE_CHANNELS.forEach((channel) => {
    source.addEventListener(channel, (message) => {
      const data = (message as MessageEvent).data;
      emit(channel, data ? JSON.parse(String(data)) : {});
    });
  });
  source.onerror = () => {
    source.close();
    if (events === source) events = null;
    window.clearTimeout(eventsTimer);
    eventsTimer = window.setTimeout(() => void bootEvents(), 1500);
  };
}

function openEvents(url: string, headers: Record<string, string>): void {
  if (events && events.readyState !== EventSource.CLOSED) return;
  events?.close();
  const target = `${url}/rpc/events`;
  events = isWeb()
    ? new EventSource(target, { withCredentials: true })
    : new EventSource(`${target}?token=${encodeURIComponent(headers.Authorization?.replace("Bearer ", "") || "")}`);
  attachEvents(events);
}

async function loadCloud(): Promise<CloudInfo | null> {
  if (cloud) return cloud.token ? cloud : null;
  if (!window.bridge) return { url: "", token: "", extensionToken: "" };
  try {
    cloud = await window.bridge.invoke("cloud:info") as CloudInfo;
    return cloud.token ? cloud : null;
  } catch {
    return null;
  }
}

async function httpCall<T>(channel: string, args: unknown[]): Promise<T> {
  const info = isWeb() ? { url: "", token: "" } : await loadCloud();
  const url = isWeb() ? "/rpc" : `${info?.url.replace(/\/+$/, "")}/rpc`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (info?.token) headers.Authorization = `Bearer ${info.token}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ channel, args })
  });
  if (response.status === 401 && isWeb()) {
    const gate = document.getElementById("login-gate");
    if (gate) gate.hidden = false;
    throw new Error(t("login with discord"));
  }
  const data = await response.json().catch(() => ({})) as { result?: T; error?: string };
  if (!response.ok) throw new Error(data.error || `rpc ${response.status}`);
  return data.result as T;
}

export async function bootEvents(): Promise<void> {
  if (isWeb()) {
    openEvents("", {});
    return;
  }
  const info = await loadCloud();
  if (info?.url && info.token) openEvents(info.url.replace(/\/+$/, ""), { Authorization: `Bearer ${info.token}` });
}

export function call<T = void>(channel: string, ...args: unknown[]): Promise<T> {
  if (channel === "app:open-external" && isWeb()) {
    const url = String(args[0] || "");
    if (/^https?:\/\//.test(url)) window.open(url, "_blank", "noopener");
    return Promise.resolve() as Promise<T>;
  }
  if (!window.bridge) return httpCall<T>(channel, args);
  if (LOCAL_CHANNELS.has(channel)) return window.bridge.invoke(channel, ...args) as Promise<T>;
  return (async () => {
    const info = await loadCloud();
    if (info?.token && info.url) return httpCall<T>(channel, args);
    return window.bridge!.invoke(channel, ...args) as Promise<T>;
  })();
}

export function mediaSrc(url: string): string {
  if (!url) return "";
  if (url.startsWith("chix:")) return url;
  let path = url;
  try {
    if (!url.startsWith("/")) {
      const parsed = new URL(url, location.href);
      path = parsed.pathname + parsed.search;
    }
  } catch {
    return url;
  }
  if (!path.startsWith("/media/")) return url;
  if (isWeb()) return path;
  const token = cloud?.token || "";
  const base = (cloud?.url || "").replace(/\/+$/, "");
  if (!base) return path;
  const abs = `${base}${path}`;
  if (!token) return abs;
  return `${abs}${abs.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

export function listen<T = void>(channel: string, listener: (payload: T) => void): void {
  const list = listeners.get(channel) || [];
  list.push(listener as (payload: unknown) => void);
  listeners.set(channel, list);
  window.bridge?.on(channel, (payload) => listener(payload as T));
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  if (!events || events.readyState === EventSource.CLOSED) {
    events = null;
    void bootEvents();
  }
});
